import type { Branding } from "@esi/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { AccountSettings } from "./account/AccountSettings.tsx";
import { useApplySavedPreferences } from "./account/preferences.ts";
import { Backoffice } from "./admin/Backoffice.tsx";
import { api } from "./api.ts";
import { ApiError } from "./api-errors.ts";
import { ArtefactLayout } from "./conversation/artefacts/ArtefactLayout.tsx";
import { ConversationFiles } from "./conversation/attachments/ConversationFiles.tsx";
import { clearAttachmentDrafts } from "./conversation/attachments/controller.ts";
import { ConversationsPage } from "./conversation/ConversationsPage.tsx";
import { ConversationView } from "./conversation/ConversationView.tsx";
import { ChatCacheProvider } from "./conversation/chat-cache.tsx";
import { MyFeedbackPage } from "./feedback/MyFeedbackPage.tsx";
import { Home } from "./home/Home.tsx";
import { useMe } from "./me.ts";
import { ProjectHome } from "./projects/ProjectHome.tsx";
import { ProjectList } from "./projects/ProjectList.tsx";
import { ProjectView } from "./projects/ProjectView.tsx";
import { SourceViewerProvider } from "./projects/SourceViewer.tsx";
import { navigate, type Route, useRoute } from "./router.tsx";
import { SessionProvider } from "./session.tsx";
import { AccountLinkPage } from "./shell/AccountLinkPage.tsx";
import { AppShell } from "./shell/AppShell.tsx";
import { BuildRefresh } from "./shell/BuildRefresh.tsx";
import { ConsentPage } from "./shell/ConsentPage.tsx";
import { DevicePage, deviceSignInPath } from "./shell/DevicePage.tsx";
import { markIdpRefreshDue } from "./shell/idp-refresh.ts";
import { KnowledgePage, NotFoundPage } from "./shell/Placeholders.tsx";
import { SignIn, StandaloneState } from "./shell/SignIn.tsx";
import { useForgetOnIdentityChange, useRefreshOnRenewal } from "./shell/session-cache.ts";
import {
  resetSessionEnded,
  sessionEndedAt,
  signInNavigation,
  useSessionEnded,
  useSessionRenewals,
} from "./shell/session-ended.ts";
import { useSessionWatch } from "./shell/session-watch.ts";
import { useTaskFocus } from "./tasks/queries.ts";
import { MyTasksPage } from "./tasks/TaskList.tsx";
import { useClientTelemetry } from "./telemetry/client-telemetry.ts";
import { applyTenantTheme } from "./theme/tenant.ts";
import { ThemeProvider } from "./theme/theme.tsx";

export function App() {
  // Re-renders the whole tree when the language changes, so every string follows it.
  useTranslation();
  const route = useRoute();
  return (
    <ThemeProvider>
      <TooltipProvider delayDuration={300}>
        {/* The pages of the links ESI emails work signed in or not: no session gate. */}
        {route.name === "invitation" ? (
          <AccountLinkPage key="invite" purpose="invite" />
        ) : route.name === "resetPassword" ? (
          <AccountLinkPage key="reset" purpose="reset" />
        ) : route.name === "console" ? (
          // The console has its own website (operator-identity-pool D1): a former console
          // address here is a plain not-found, signed in or not, and never points there.
          <ConsoleNotFound />
        ) : (
          <Signed />
        )}
        {/* Toasts render inline in #root, outside every screen's `page`: masked with it, as a
            toast may name a file, a folder or a project. */}
        <div data-esi-content="page" className="contents">
          <Toaster position="bottom-right" />
        </div>
        <BuildRefresh />
      </TooltipProvider>
    </ThemeProvider>
  );
}

function Signed() {
  const { t } = useTranslation();
  const route = useRoute();
  // The browser holds only an httpOnly session cookie; this tells us whether it is live.
  const session = useQuery({
    queryKey: ["session"],
    queryFn: async () => (await api.api.auth.session.$get()).json(),
    retry: 1,
  });
  const principal = session.data?.authenticated ? session.data.principal : null;
  const user =
    session.data?.authenticated && principal?.type === "user"
      ? {
          csrfToken: session.data.csrfToken,
          userId: principal.userId,
          tenantId: principal.tenantId,
          entityId: principal.entityId,
          roles: principal.roles,
        }
      : null;
  // The session check also says when a federated session's IdP groups are due for renewal.
  useEffect(() => {
    if (session.data?.authenticated) {
      markIdpRefreshDue("idpRefreshDue" in session.data ? session.data.idpRefreshDue : false);
    }
  }, [session.data]);
  // Once the session ends under unsaved work, a later session check (window focus) must not
  // swap the shell for the sign-in page: the shell stays, with its "Sign in again" banner.
  const ended = useSessionEnded();
  const lastUser = useRef(user);
  if (user) {
    lastUser.current = user;
  }
  const shown = user ?? (ended ? lastUser.current : null);
  // Client telemetry on signed-out pages: the instance's `telemetry.clientEvents` (the session
  // answer); a user's shell decides with the tenant's (`ClientTelemetryGate`).
  useClientTelemetry(
    !shown && session.data && "clientTelemetry" in session.data
      ? session.data.clientTelemetry
      : undefined,
  );
  // The session check says signed in again (another tab): the banner and its redirect go.
  useEffect(() => {
    if (user && ended && session.dataUpdatedAt > sessionEndedAt()) {
      resetSessionEnded();
    }
  }, [user, ended, session.dataUpdatedAt]);
  // While the shell is kept for an ended session, its cache belongs to the same user still.
  const identity =
    principal === null
      ? shown
        ? `user:${shown.tenantId}:${shown.userId}`
        : null
      : principal.type === "user"
        ? `user:${principal.tenantId}:${principal.userId}`
        : `operator:${principal.operatorId}`;
  // Identity must clear first: token renewal alone may preserve this principal's text.
  useForgetOnIdentityChange(identity, () => clearAttachmentDrafts({ notify: false }));
  // The tenant's branding belongs to a live session of one account: reset with it, before
  // anything renders for the next state (an ended session keeps the shell, not the branding).
  useResetBrandingOnChange(ended ? null : identity);
  useForgetAttachmentSession(shown ? `${identity}:${shown.csrfToken}` : null);
  useRefreshOnRenewal();

  if (session.isPending) {
    return null;
  }
  // A native client's sign-in (openspec change native-auth-server): ESI's sign-in page goes
  // back to the authorization once signed in, unless the client asked for a fresh sign-in.
  const signedIn = session.data?.authenticated === true;
  if (route.name === "signIn" && signedIn && !route.reauth) {
    return <LeaveFor href={route.returnTo} />;
  }
  if (route.name === "signIn" && !session.isError) {
    return <SignIn />;
  }
  // The device page (native-device-and-cimd): outside the shell, after ESI's sign-in.
  if (route.name === "device") {
    if (user) {
      return <DevicePage initialCode={route.userCode} csrfToken={user.csrfToken} />;
    }
    if (!signedIn && !session.isError) {
      return <LeaveFor href={deviceSignInPath(route.userCode ?? "")} />;
    }
  }
  // The consent screen: outside the shell, for a signed-in user only.
  if (route.name === "connect" && user) {
    return <ConsentPage uid={route.uid} csrfToken={user.csrfToken} />;
  }
  if (session.isError) {
    const network = !(session.error instanceof ApiError);
    return (
      <StandaloneState title={t("app.unreachable")}>
        <Notice
          tone="danger"
          action={
            <Button size="sm" variant="outline" onClick={() => void session.refetch()}>
              {t("app.retry")}
            </Button>
          }
        >
          {network ? t("errors.network") : session.error.message}
        </Notice>
      </StandaloneState>
    );
  }
  if (shown) {
    return (
      <SessionProvider key={identity} value={shown}>
        <SavedPreferences />
        <ClientTelemetryGate />
        <TenantBranding active={!ended} />
        <ChatCacheProvider>
          <SourceViewerProvider>
            <AppShell route={route}>
              <Routes route={route} />
            </AppShell>
          </SourceViewerProvider>
        </ChatCacheProvider>
      </SessionProvider>
    );
  }
  // Operators sign in on the console website only: the tenant application never holds their
  // session (the API ends one it finds here).
  return <SignIn />;
}

/** A former console address on the tenant host: not found, with no way onward. */
function ConsoleNotFound() {
  const { t } = useTranslation();
  return (
    <StandaloneState title={t("placeholder.notFound.title")}>
      <p className="text-sm leading-relaxed text-fg-secondary">{t("placeholder.notFound.body")}</p>
    </StandaloneState>
  );
}

/** Leaves the app for `href` (a full page load: it may be an API route), once. */
function LeaveFor({ href }: { href: string }) {
  useEffect(() => {
    signInNavigation.go(href);
  }, [href]);
  return null;
}

/**
 * Client telemetry while a user's shell is shown (an ended session's kept shell included): the
 * tenant's `telemetry.clientEvents` from `/api/me`; unknown until it arrives (events wait).
 */
function ClientTelemetryGate() {
  const me = useMe();
  useClientTelemetry(me.data?.clientTelemetry);
  return null;
}

/** Applies the account's saved language and theme, and watches the session while signed in. */
function SavedPreferences() {
  useTaskFocus();
  useApplySavedPreferences();
  useSessionWatch();
  return null;
}

/**
 * Resets the tenant theme (accents and logo) during the render where `scope` changes: another
 * account, sign-out, an operator, or the session ending. The views of the next state never show
 * the previous tenant's branding (D5 of `brand-logo-held`).
 */
function useResetBrandingOnChange(scope: string | null) {
  const previous = useRef(scope);
  if (previous.current !== scope) {
    // Leaving a scope resets; entering one from none (the first session check) has nothing to.
    const left = previous.current !== null;
    previous.current = scope;
    if (left) {
      applyTenantTheme({});
    }
  }
}

/**
 * Applies the signed-in tenant's branding from `/api/me` (openspec change `tenant-branding`): the
 * accents and the logo per theme, named by the display name or else the tenant's name. Users
 * only (operators never render it); an older API without `branding` applies nothing.
 */
function TenantBranding({ active }: { active: boolean }) {
  const me = useMe();
  const branding = me.data?.branding;
  const tenantName = me.data?.tenantName;
  const key = active && branding ? JSON.stringify([branding, tenantName]) : null;
  useEffect(() => {
    if (!key) {
      return;
    }
    const [b, name] = JSON.parse(key) as [Branding, string | undefined];
    applyTenantTheme({
      ...(b.accentLight ? { accent: b.accentLight } : {}),
      ...(b.accentDark ? { accentDark: b.accentDark } : {}),
      ...(b.logoLight ? { logoUrl: b.logoLight.url } : {}),
      ...(b.logoDark ? { logoUrlDark: b.logoDark.url } : {}),
      logoLabel: b.displayName ?? name ?? "",
    });
  }, [key]);
  return null;
}

/** A renewed browser session starts a fresh attachment memory/cache lifecycle. */
function useForgetAttachmentSession(session: string | null) {
  const client = useQueryClient();
  const previous = useRef(session);
  if (previous.current !== session) {
    previous.current = session;
    clearAttachmentDrafts({ preserveText: session !== null, notify: false });
    client.removeQueries({
      predicate: (q) =>
        q.queryKey[0] === "conversation-attachments" || q.queryKey[0] === "citation-passage",
    });
  }
}

/** The tab title of the areas whose page does not name itself (projects and the wiki do). */
function useAreaTitle(route: Route) {
  const { t } = useTranslation();
  const titles: Partial<Record<Route["name"], string>> = {
    projects: t("nav.projects"),
    conversations: t("nav.conversations"),
    tasks: t("tasks.myTitle"),
    knowledge: t("nav.knowledge"),
    backoffice: t("nav.backoffice"),
    account: t("accountSettings.title"),
    notFound: t("placeholder.notFound.title"),
  };
  const named =
    route.name === "projectTask" ||
    route.name === "projectHome" ||
    route.name === "projectConversation" ||
    route.name === "conversation" ||
    route.name === "projectSettings" ||
    route.name === "wiki" ||
    route.name === "wikiNew" ||
    route.name === "wikiEdit" ||
    route.name === "wikiDraft" ||
    route.name === "wikiChange" ||
    route.name === "wikiHistory" ||
    route.name === "wikiCompare";
  useDocumentTitle(named ? undefined : (titles[route.name] ?? null), !named);
}

function Routes({ route }: { route: Route }) {
  useAreaTitle(route);
  switch (route.name) {
    case "home":
      return <Home />;
    case "projects":
      return <ProjectList />;
    case "projectTask":
      return (
        <ProjectHome
          key={route.projectId}
          projectId={route.projectId}
          tab="tasks"
          taskId={route.taskId}
        />
      );
    case "projectHome":
      return <ProjectHome key={route.projectId} projectId={route.projectId} tab={route.tab} />;
    case "wiki":
    case "wikiNew":
    case "wikiEdit":
    case "wikiDraft":
    case "wikiChange":
    case "wikiHistory":
    case "wikiCompare":
      return (
        <ProjectHome key={route.projectId} projectId={route.projectId} tab="wiki" wiki={route} />
      );
    case "projectConversation":
      return (
        <ProjectView
          key={`${route.projectId}:${route.conversationId}`}
          projectId={route.projectId}
          conversationId={route.conversationId}
        />
      );
    case "conversation":
      return (
        <div className="flex min-h-0 flex-1 flex-col">
          <ArtefactLayout
            key={route.conversationId}
            scope={{ projectId: null, conversationId: route.conversationId }}
            conversation={
              <ConversationView
                conversationId={route.conversationId}
                headerExtra={
                  <ConversationFiles
                    scope={{ projectId: null, conversationId: route.conversationId }}
                  />
                }
              />
            }
          />
        </div>
      );
    case "redirect":
      return <Redirect to={route.to} />;
    case "projectSettings":
      return (
        <ProjectHome
          key={route.projectId}
          projectId={route.projectId}
          tab="settings"
          settingsGroup={route.group}
        />
      );
    case "conversations":
      return <ConversationsPage />;
    case "tasks":
      return <MyTasksPage />;
    case "knowledge":
      return <KnowledgePage />;
    case "backoffice":
      return (
        <Backoffice
          section={route.section}
          projectId={route.projectId}
          settingsGroup={route.settingsGroup}
        />
      );
    case "account":
      return route.view === "feedback" ? <MyFeedbackPage /> : <AccountSettings />;
    case "invitation":
    case "resetPassword":
    case "notFound":
    // Rendered by `App` before the session gate; kept for exhaustiveness.
    case "console":
      return <NotFoundPage />;
  }
}

/** A former URL: replaced by its new one (history entry included) for one release. */
function Redirect({ to }: { to: string }) {
  useEffect(() => {
    navigate(to, { replace: true });
  }, [to]);
  return null;
}
